"use client";

import { Slider } from "@/components/ui/slider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { StarGold } from "@/components/shared/svg/Icons";
import { useCategoriesTree } from "@/features/categories/hook/useCategories";
import { Category } from "@/types/category.types";

export interface CatalogFiltersState {
    minPrice?: number | undefined;
    maxPrice?: number | undefined;
    categoryIds?: string[] | undefined;
    sort?: string | undefined;
    inStock?: boolean | undefined;
    rating?: number | undefined;
    offerStatus?: string | undefined;
}

interface CatalogFiltersProps {
    filters: CatalogFiltersState;
    setFilters: React.Dispatch<React.SetStateAction<CatalogFiltersState>>;
}

export const CatalogFilters = ({ filters, setFilters }: CatalogFiltersProps) => {
    const { data: categoriesResponse } = useCategoriesTree();
    const categories = Array.isArray(categoriesResponse) 
        ? categoriesResponse 
        : (categoriesResponse as any)?.data || [];

    return (
        <aside className="rounded-3xl border border-primary-200 bg-transparent p-4 lg:p-6 space-y-6 lg:space-y-8">

            {/* CATEGORIES */}
            <div className="space-y-4">
                <h3 className="flex items-center justify-between text-base lg:text-lg font-medium">
                    Catégories <ChevronRight size={18} />
                </h3>

                <RadioGroup
                    value={filters.categoryIds?.[0] || ""}
                    onValueChange={(val) => setFilters(prev => ({ ...prev, categoryIds: [val] }))}
                    className="space-y-3"
                >
                    {categories.map((cat: Category) => (
                        <label
                            key={cat.id}
                            className="flex items-center gap-3 text-gray-600 cursor-pointer text-sm lg:text-base"
                        >
                            <RadioGroupItem value={cat.id} />
                            {cat.name}
                        </label>
                    ))}
                </RadioGroup>
            </div>

            {/* OFFER STATUS */}
            <div className="space-y-4">
                <h3 className="flex items-center justify-between text-base lg:text-lg font-medium">
                    Statut de l&apos;offre <ChevronRight size={18} />
                </h3>

                <RadioGroup
                    value={filters.offerStatus || "all"}
                    onValueChange={(val) => setFilters(prev => ({ ...prev, offerStatus: val }))}
                    className="space-y-3"
                >
                    {(
                        [
                            ["all", "Tout"],
                            ["sale", "On Sale"],
                            ["promo", "Promo"],
                        ] as const
                    ).map(([value, label]) => (
                        <label
                            key={value}
                            className="flex items-center gap-3 text-gray-600 cursor-pointer text-sm lg:text-base"
                        >
                            <RadioGroupItem value={value} />
                            {label}
                        </label>
                    ))}
                </RadioGroup>
            </div>

            {/* PRICE */}
            <div className="space-y-4">
                <h3 className="flex items-center justify-between text-base lg:text-lg font-medium">
                    Prix <ChevronRight size={18} />
                </h3>

                <Slider
                    value={[filters.minPrice ?? 100, filters.maxPrice ?? 5000]}
                    min={100}
                    max={5000}
                    step={50}
                    onValueChange={([min, max]) => setFilters((prev) => ({ ...prev, minPrice: min, maxPrice: max }))}
                />

                <p className="text-center text-xs lg:text-sm text-gray-500">
                    Prix 100DA – 5000DA
                </p>
            </div>

            {/* RATINGS */}
            <div className="space-y-4">
                <h3 className="flex items-center justify-between text-base lg:text-lg font-medium">
                    Ratings <ChevronRight size={18} />
                </h3>

                <RadioGroup className="space-y-3">
                    {[5, 4, 3, 2, 1].map((stars) => (
                        <label
                            key={stars}
                            className="flex items-center gap-3 cursor-pointer"
                            onClick={() => setFilters((prev) => ({ ...prev, rating: stars }))}
                        >
                            <RadioGroupItem value={String(stars)} checked={filters.rating === stars} />
                            <div className="flex gap-1">
                                {[...Array(5)].map((_, i) => (
                                    <StarGold
                                        key={i}
                                        className={cn(
                                            "w-4 h-4 opacity-30",
                                            i < stars && "opacity-100"
                                        )}
                                    />
                                ))}
                            </div>
                        </label>
                    ))}
                </RadioGroup>
            </div>
        </aside>
    );
};